import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pprApi, usersApi, PPR_ACCOUNTING_TRANSITION, type PprJob, type PprStatus } from '@/lib/apiClient';
import { fmtUsd, fmtDate, STATUS_COLORS, REVIEW_TYPE_COLORS, PPR_PIPELINE_STATUSES } from './pprShared';
import CancelReviewActions from './detail/CancelReviewActions';
import CancellationRecord from './detail/CancellationRecord';
import ContactsTab from './detail/ContactsTab';
import FinancialsTab from './detail/FinancialsTab';
import ReportTab from './detail/ReportTab';
import NotesHistoryTab from './detail/NotesHistoryTab';

type TabKey = 'contacts' | 'financials' | 'report' | 'notes';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'contacts',   label: 'Contacts & Notifications' },
  { key: 'financials', label: 'Financial Data' },
  { key: 'report',     label: 'Report' },
  { key: 'notes',      label: 'Notes & History' },
];

const css = `
  .ppr-detail-back {
    margin-bottom: var(--ew-s-3);
  }
  .ppr-detail-back a {
    color: var(--ew-blue, #1875BD);
    text-decoration: none;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
  }
  .ppr-detail-back a:hover { text-decoration: underline; }

  .ppr-detail-header {
    margin-bottom: var(--ew-s-4);
  }
  .ppr-detail-header h1 {
    font: var(--ew-fw-semibold) var(--ew-fs-h3)/1.1 var(--ew-font);
    color: var(--ew-dark);
    margin: 0 0 6px 0;
  }
  .ppr-detail-meta {
    color: var(--ew-n-600);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    margin-bottom: var(--ew-s-3);
  }
  .ppr-detail-badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: var(--ew-s-3);
  }

  /* Pipeline-stage control */
  .ppr-status-control {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .ppr-status-label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase;
    letter-spacing: var(--ew-ls-caption);
    color: var(--ew-n-500);
  }
  .ppr-status-control select {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    padding: 5px 8px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
    cursor: pointer;
  }
  .ppr-status-error {
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.3 var(--ew-font);
    color: #8F2414;
    max-width: 320px;
  }

  /* Tab nav */
  .ppr-tab-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    border-bottom: 1px solid var(--ew-n-300);
    margin-bottom: var(--ew-s-4);
  }
  .ppr-tab {
    background: transparent;
    border: none;
    padding: 10px 16px;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    color: var(--ew-n-600);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color var(--ew-dur-fast) var(--ew-ease),
                border-color var(--ew-dur-fast) var(--ew-ease);
  }
  .ppr-tab:hover { color: var(--ew-dark); }
  .ppr-tab.active {
    color: var(--ew-dark);
    border-bottom-color: var(--ew-dark);
  }
  .ppr-tab-count {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 7px;
    border-radius: var(--ew-r-pill);
    background: var(--ew-n-100);
    color: var(--ew-n-600);
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
  }
  .ppr-tab.active .ppr-tab-count {
    background: var(--ew-dark);
    color: #fff;
  }
  .ppr-tab-count.warn { background: #FBE4DF; color: #8F2414; }
  .ppr-tab-count.ok   { background: #E6F2EB; color: #015A28; }

  .ppr-tab-body {
    /* Tabs may set their own padding. Keep this minimal. */
  }

  /* ─── Contacts tab ─────────────────────────────────────────────────────── */
  .ppr-contacts-summary {
    color: var(--ew-n-600);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    margin-bottom: var(--ew-s-3);
  }
  .ppr-contacts-overdue   { color: #DB7A25; font-weight: 600; }
  .ppr-contacts-escalated { color: #8F2414; font-weight: 600; }

  .ppr-contact-list {
    display: flex;
    flex-direction: column;
    gap: var(--ew-s-2);
  }
  .ppr-contact-row {
    display: grid;
    grid-template-columns: 200px 1fr 1fr auto;
    gap: var(--ew-s-3);
    align-items: start;
    padding: var(--ew-s-3);
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
  }
  .ppr-contact-row-overdue   { border-left: 3px solid #DB7A25; }
  .ppr-contact-row-escalated { border-left: 3px solid #8F2414; }
  @media (max-width: 800px) {
    .ppr-contact-row { grid-template-columns: 1fr; }
  }

  .ppr-contact-position {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.25 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-contact-identity {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .ppr-contact-name {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-dark);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ppr-contact-email {
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.3 var(--ew-font);
    color: var(--ew-n-600);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ppr-muted { color: var(--ew-n-400); font-style: italic; }

  .ppr-contact-state {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.3 var(--ew-font);
    color: var(--ew-n-600);
  }
  .ppr-contact-state-line {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .ppr-contact-warning {
    color: #8F2414;
    font-weight: 600;
    margin-top: 2px;
  }
  .ppr-contact-meta { color: var(--ew-n-500); font-size: 11px; }

  .ppr-contact-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: stretch;
  }
  .ppr-contact-edit-actions {
    display: flex;
    gap: 6px;
    margin-top: 4px;
  }
  .ppr-contact-error {
    grid-column: 1 / -1;
    margin-top: 8px;
    padding: 8px 10px;
    background: #FBE4DF;
    color: #8F2414;
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
  }

  .ppr-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .ppr-dot-on  { background: #027835; }
  .ppr-dot-off { background: var(--ew-n-300); }

  .ppr-input {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    padding: 6px 9px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
    width: 100%;
    box-sizing: border-box;
  }
  .ppr-linkbtn {
    background: none;
    border: none;
    padding: 0;
    color: var(--ew-blue, #1875BD);
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    cursor: pointer;
    text-align: left;
  }
  .ppr-linkbtn:hover { text-decoration: underline; }

  .btn-sm {
    font-size: 12px;
    padding: 5px 10px;
  }

  .ppr-empty {
    text-align: center;
    color: var(--ew-n-400);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    padding: var(--ew-s-4) 0;
  }

  /* ─── Financials tab ───────────────────────────────────────────────────── */
  .ppr-fin-section { margin-bottom: var(--ew-s-5); }
  .ppr-fin-section h3 {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/1 var(--ew-font);
    color: var(--ew-dark);
    margin: 0 0 var(--ew-s-3) 0;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--ew-n-200);
  }
  .ppr-fin-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: var(--ew-s-3);
  }
  .ppr-fin-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .ppr-fin-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    color: var(--ew-n-600);
  }
  .ppr-fin-field .ppr-input { width: 100%; }
  .ppr-fin-field .ppr-derived {
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.3 var(--ew-font);
    color: var(--ew-n-500);
    margin-top: 2px;
  }
  .ppr-fin-actions {
    display: flex;
    gap: var(--ew-s-3);
    align-items: center;
    margin-top: var(--ew-s-4);
    padding-top: var(--ew-s-3);
    border-top: 1px solid var(--ew-n-200);
  }
  .ppr-fin-saved {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    color: #015A28;
  }
  .ppr-fin-error {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1.4 var(--ew-font);
    color: #8F2414;
  }
  .ppr-fin-complete-banner {
    margin-bottom: var(--ew-s-4);
    padding: 10px 14px;
    background: #E6F2EB;
    color: #015A28;
    border: 1px solid rgba(2, 120, 53, 0.3);
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }

  /* ─── Report tab ───────────────────────────────────────────────────────── */
  .ppr-report-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--ew-s-3);
  }
  .ppr-report {
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    padding: var(--ew-s-5);
    max-width: 850px;
    margin: 0 auto;
    color: var(--ew-dark);
  }
  .ppr-report h2 {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.1 var(--ew-font);
    margin: 0 0 4px 0;
  }
  .ppr-report h3 {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/1 var(--ew-font);
    margin: var(--ew-s-4) 0 var(--ew-s-2) 0;
    padding-bottom: 4px;
    border-bottom: 1px solid var(--ew-n-200);
  }
  .ppr-report-meta {
    color: var(--ew-n-600);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    margin-bottom: var(--ew-s-3);
  }
  .ppr-report-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--ew-s-3);
  }
  .ppr-report-field {
    display: flex;
    justify-content: space-between;
    gap: var(--ew-s-2);
    padding: 4px 0;
    border-bottom: 1px dotted var(--ew-n-200);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .ppr-report-field .label { color: var(--ew-n-600); }
  .ppr-report-field .value { font-weight: 600; }
  .ppr-report-narrative {
    white-space: pre-wrap;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-report-contacts {
    width: 100%;
    border-collapse: collapse;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .ppr-report-contacts th, .ppr-report-contacts td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid var(--ew-n-200);
  }
  .ppr-report-contacts th {
    font-weight: 600;
    color: var(--ew-n-600);
    background: var(--ew-n-50);
  }

  @media print {
    .ppr-detail-back, .ppr-tab-nav, .ppr-report-toolbar { display: none !important; }
    .ppr-detail-header { display: none !important; }
    .ppr-report {
      border: none;
      max-width: 100%;
      padding: 0;
    }
    /* Page-break friendliness */
    .ppr-report h3 { page-break-after: avoid; }
    .ppr-report-field { page-break-inside: avoid; }
  }

  /* ─── Notes & History tab ──────────────────────────────────────────────── */
  .ppr-notes-textarea {
    width: 100%;
    min-height: 200px;
    padding: 10px 12px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    color: var(--ew-dark);
    background: #fff;
    resize: vertical;
    box-sizing: border-box;
  }
  .ppr-history-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ew-s-2);
  }
  .ppr-history-item {
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    padding: 10px 14px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-history-item .label { color: var(--ew-n-600); margin-right: 6px; }
`;

/**
 * /ppr/:jobId — full job detail view.
 *
 * Four tabs:
 *   1. Contacts & Notifications — track notifications and responses from the 9 positions
 *   2. Financial Data           — edit 21 financial fields + mark financials complete
 *   3. Report                   — print-friendly summary view
 *   4. Notes & History          — coordinator notes + audit timestamps
 *
 * Tab state is held in-page (useState) but synced to the URL hash
 * (`/ppr/123#financials`) for shareable links and reload-stability.
 */
export default function PprJobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const id = Number(jobId);

  // Hash-synced tab state. Default = contacts.
  const [tab, setTab] = useState<TabKey>(() => hashToTab(window.location.hash));
  useEffect(() => {
    // Update URL hash without a navigation when tab changes.
    const expected = `#${tab}`;
    if (window.location.hash !== expected) {
      window.history.replaceState(null, '', expected);
    }
  }, [tab]);
  // Listen for back/forward changing the hash.
  useEffect(() => {
    const handler = () => setTab(hashToTab(window.location.hash));
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const jobQuery = useQuery({
    queryKey: ['ppr', 'job', id],
    queryFn: () => pprApi.getJob(id),
    enabled: !!id,
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  // Gate on the PERMISSION the API enforces (ppr.manage), not on role names —
  // see the same fix in PprDashboard. Admin bypasses the permissions guard
  // server-side, so it stays an explicit escape hatch here too.
  const canEdit = !!me && (me.roles?.includes('Admin') || !!me.permissions?.includes('ppr.manage'));

  /**
   * The Financial Data tab has its own, wider gate: Accounting hold
   * `ppr.financials.edit` and may fill that tab in without being able to touch
   * anything else on the job (issue #312 — the stage is named "Awaiting
   * Financials" and waits on them, but every input was disabled with no Save
   * button and no message, which reads as a broken screen rather than a denial).
   *
   * ⚠️ Must stay in step with the API: PPR_FINANCIAL_FIELDS + the any-of guard
   * on PATCH /ppr/jobs/:id. Widening this alone would just move the failure from
   * a disabled input to a 403 on save.
   */
  const canEditFinancials = canEdit || !!me?.permissions?.includes('ppr.financials.edit');

  if (jobQuery.isLoading) {
    return (
      <>
        <style>{css}</style>
        <p>Loading…</p>
      </>
    );
  }
  if (jobQuery.isError) {
    return (
      <>
        <style>{css}</style>
        <p style={{ color: '#8F2414' }}>
          Failed to load: {String((jobQuery.error as Error)?.message ?? 'unknown')}
        </p>
      </>
    );
  }
  if (!jobQuery.data) return null;

  const job = jobQuery.data;
  // Accounting (ppr.view, no ppr.manage) may make exactly one move, and only from
  // Awaiting Financials — so the Stage control appears for them on precisely the
  // jobs where they have something to do, and nowhere else.
  const canAdvanceToAccounting = !canEdit
    && !!me?.permissions?.includes('ppr.view')
    && job.status === PPR_ACCOUNTING_TRANSITION.from;
  const isCancelled = job.status === 'Cancelled';
  const rt = REVIEW_TYPE_COLORS[job.reviewType];
  const st = STATUS_COLORS[job.status];

  // Tab badges
  const contactsBadge =
    `${job.contactsResponded ?? 0}/${job.contactsTotal ?? job.contacts?.length ?? 9}`;
  const contactsBadgeClass =
    (job.contactsEscalated ?? 0) > 0 ? 'warn'
      : (job.contactsOverdue ?? 0) > 0 ? 'warn'
      : (job.contactsResponded ?? 0) === (job.contactsTotal ?? 9) ? 'ok'
      : '';
  const finBadge = job.financialsComplete ? '✓' : null;

  return (
    <>
      <style>{css}</style>

      <div className="ppr-detail-back">
        <Link to="/ppr">← Back to pipeline</Link>
      </div>

      <div className="ppr-detail-header">
        <h1>{job.jobName}</h1>
        <p className="ppr-detail-meta">
          Job #{job.jobNumber} · {fmtUsd(job.jobValue)} · closed {fmtDate(job.closeDate)}
        </p>
        <div className="ppr-detail-badges">
          <span className="ppr-badge" style={{ background: rt.bg, color: rt.text, padding: '4px 10px', borderRadius: 'var(--ew-r-pill)' }}>{job.reviewType}</span>
          {/* A cancelled review shows a plain badge, never the Stage dropdown:
              the API refuses stage moves while cancelled, so a working-looking
              select would only ever produce an error. Reopen first — the button
              for that is in CancelReviewActions below. */}
          {isCancelled || !(canEdit || canAdvanceToAccounting)
            ? <span className="ppr-badge" style={{ background: st.bg, color: st.text, padding: '4px 10px', borderRadius: 'var(--ew-r-pill)' }}>{job.status}</span>
            : <StatusControl job={job} canManage={canEdit} />}
        </div>
      </div>

      {/* Rendered for EVERY viewer, and on the presence of a cancellation rather
          than the current status. A ppr.view user (Accounting) can open a
          cancelled review, and while the reason lived in notes they could read it
          there; gating the only thing that renders it on canEdit hid it from
          them entirely. It also keeps the record visible AFTER a reopen, which is
          the point of retaining cancelled_* at all. */}
      <CancellationRecord job={job} />
      {canEdit && <CancelReviewActions job={job} />}

      <div className="ppr-tab-nav" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`ppr-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'contacts' && (
              <span className={`ppr-tab-count ${contactsBadgeClass}`}>{contactsBadge}</span>
            )}
            {t.key === 'financials' && finBadge && (
              <span className="ppr-tab-count ok">{finBadge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="ppr-tab-body" role="tabpanel">
        {/* Contact actions are disabled on a cancelled review — "Mark Notified"
            drives the survey emails, so leaving them live would let a stopped
            review contact nine people, and would mutate the very history the
            cancel dialog promises to preserve. PprService.updateContact enforces
            the same rule for all three contact endpoints. */}
        {tab === 'contacts'   && <ContactsTab     job={job} canEdit={canEdit && !isCancelled} />}
        {tab === 'financials' && (
          <FinancialsTab
            job={job}
            canEdit={canEditFinancials}
            // Mirrors PprService.markFinancialsComplete: a financials-only
            // reader is scoped to Awaiting Financials; a manager is not — and
            // nobody may complete a CANCELLED review, which the service now
            // refuses. That call fires the coordinator-ready email, so leaving
            // the button visible on a terminal review was the last action here
            // that could reach a human.
            canComplete={!isCancelled && (canEdit || job.status === 'Awaiting Financials')}
          />
        )}
        {tab === 'report'     && <ReportTab       job={job} />}
        {tab === 'notes'      && <NotesHistoryTab job={job} canEdit={canEdit} />}
      </div>
    </>
  );
}

/**
 * Pipeline-stage control in the detail header. A dropdown of the pipeline stages
 * — covers advancing, moving back, or jumping. The financials gate (Report Prep /
 * Complete require financials complete) is enforced server-side; if it rejects,
 * the error shows inline and the select snaps back to the job's real status.
 */
/**
 * @param canManage false for a ppr.view-only user (Accounting). They get exactly
 *   one option — Awaiting Financials → Financials Completed by Accounting — and
 *   only while the job is actually in Awaiting Financials. The API enforces the
 *   same rule; this just avoids offering a choice that would 403.
 */
function StatusControl({ job, canManage }: { job: PprJob; canManage: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (status: PprStatus) => pprApi.setStatus(job.id, status),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['ppr'] }); // refreshes this job + the dashboard
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <span className="ppr-status-control">
      <label className="ppr-status-label">Stage</label>
      <select
        value={job.status}
        disabled={mut.isPending}
        onChange={(e) => mut.mutate(e.target.value as PprStatus)}
        title={canManage ? undefined : `You can move this to "${PPR_ACCOUNTING_TRANSITION.to}" once the numbers are settled.`}
      >
        {(canManage
          // PIPELINE stages only. Cancelled is a status but not a stage — it has
          // its own control (CancelReviewActions), because reaching it captures a
          // reason and resets the project's closeout alert. Offering it here would
          // be a one-click accidental cancel that skipped both, and the API now
          // rejects it on this endpoint anyway.
          ? PPR_PIPELINE_STATUSES
          // Current stage first so the select has a valid value, then the single
          // move this user is allowed to make.
          : [job.status, PPR_ACCOUNTING_TRANSITION.to]
        ).map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      {error && <span className="ppr-status-error">{error}</span>}
    </span>
  );
}

function hashToTab(hash: string): TabKey {
  const key = hash.replace(/^#/, '') as TabKey;
  if (key === 'contacts' || key === 'financials' || key === 'report' || key === 'notes') {
    return key;
  }
  return 'contacts';
}
